import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessagesSquare, Search, Code2, Bot, Activity } from "lucide-react";
import { useCms } from "@/lib/cms";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { resolveAutomation } from "@/lib/siteContent";
import "./AutomationSection.css";

const flow = [
  { icon: MessagesSquare, label: "상담", desc: "고객 비즈니스 진단" },
  { icon: Search, label: "분석", desc: "요구사항 · 데이터 흐름 설계" },
  { icon: Code2, label: "개발", desc: "맞춤형 웹 · 플랫폼 구축" },
  { icon: Bot, label: "자동화", desc: "AI · 알림 · CRM 통합" },
  { icon: Activity, label: "운영", desc: "지속 유지보수 · 그로스" },
];

const FALLBACK_AUTO_IMG = "/images/automation-section.png";

function pickAutomationImage(cms) {
  const cmsUrl = resolveMediaUrl(cms.media_automation_image);
  return cmsUrl || FALLBACK_AUTO_IMG;
}

export default function AutomationSection() {
  const { cms } = useCms();
  const copy = resolveAutomation(cms);
  const [autoImg, setAutoImg] = useState(() => pickAutomationImage(cms));

  useEffect(() => {
    setAutoImg(pickAutomationImage(cms));
  }, [cms.media_automation_image]);

  const handleImageError = () => {
    setAutoImg((current) => (current === FALLBACK_AUTO_IMG ? current : FALLBACK_AUTO_IMG));
  };

  return (
    <section id="automation" data-testid="automation-section" className="ds-automation-section">
      <div className="ds-automation-grid-bg bg-grid-sm" aria-hidden="true" />

      <div className="ds-automation-inner max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="ds-automation-eyebrow-line" />
          <span className="ds-automation-eyebrow-label">{copy.eyebrow}</span>
        </div>

        <div className="ds-automation-layout grid grid-cols-1 gap-10 md:gap-16 items-start">
          <div className="min-w-0">
            <h2 className="ds-automation-title font-display font-medium tracking-tight text-white leading-[1.1] whitespace-pre-line">
              {copy.title}
            </h2>
            <p className="mt-8 text-base ds-automation-body max-w-lg">
              {copy.description}
            </p>

            <div className="mt-10 relative">
              <div className="ds-automation-flow-line" aria-hidden="true">
                <div className="ds-automation-flow-line-bg" />
                <motion.div
                  className="ds-automation-flow-line-pulse"
                  animate={{ y: ["-100%", "1000%"] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                />
              </div>

              <div className="space-y-6">
                {copy.steps.map((step, i) => {
                  const Icon = flow[i]?.icon || Activity;
                  return (
                    <motion.div
                      key={step.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.55, delay: i * 0.1, ease: [0.2, 0.7, 0.2, 1] }}
                      className="ds-automation-step flex gap-5 items-start group"
                      data-testid={`flow-step-${i}`}
                    >
                      <div className="ds-automation-step-icon">
                        <Icon size={18} />
                      </div>
                      <div className="pt-2">
                        <div className="flex items-baseline gap-3">
                          <span className="ds-automation-step-num">0{i + 1}</span>
                          <h3 className="ds-automation-step-label font-display text-lg font-medium">
                            {step.label}
                          </h3>
                        </div>
                        <p className="text-sm ds-automation-step-desc mt-1">{step.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
            className="min-w-0"
          >
            <div className="ds-automation-media-wrap">
              <div className="ds-automation-media">
                <img
                  src={autoImg}
                  alt="업무 자동화 워크플로"
                  data-testid="automation-image"
                  onError={handleImageError}
                />
              </div>

              <div className="ds-automation-tags">
                {["AI 챗봇", "이메일 자동", "카카오 알림", "CRM 연동"].map((t, i) => (
                  <motion.div
                    key={t}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 + i * 0.08 }}
                    className="ds-automation-tag"
                  >
                    <span className="ds-automation-tag-dot" aria-hidden="true" />
                    {t}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
